import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import { settingsService } from '../../services/settings/settings.service';
import { useAuth } from '../../hooks/useAuth';
import { validateEmail, validateRequired } from '../../utils/validators';
import type { ContactPayload } from '../../types/settings.types';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all text-sm';

const CATEGORIES = ['General question', 'Account & billing', 'Technical issue', 'Feedback', 'Partnership'];

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState<ContactPayload>({
    subject: '',
    category: CATEGORIES[0],
    message: '',
    email: user?.email ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);

  const set = <K extends keyof ContactPayload>(key: K, value: ContactPayload[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validateEmail(form.email) ?? validateRequired(form.subject, 'Subject') ?? validateRequired(form.message, 'Message');
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await settingsService.submitContact(form);
      setTicketId(res.ticketId);
    } catch {
      setError('Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketId) {
    return (
      <div className="space-y-6 max-w-xl">
        <PageHeader title="Contact support" icon={Mail} backTo="/app/settings/support" />
        <SettingCard>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4"><CheckCircle2 className="w-7 h-7 text-green-600" /></div>
            <h2 className="text-lg font-bold">Message sent</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">We'll reply to <span className="font-medium text-neutral-700">{form.email}</span> soon.</p>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-3">Reference <span className="font-mono">{ticketId}</span></p>
            <Link to="/app/settings/support" className="mt-6 px-6 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition-colors">Back to Support</Link>
          </div>
        </SettingCard>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="Contact support" subtitle="Send us a message and we'll get back to you" icon={Mail} backTo="/app/settings/support" />

      <SettingCard>
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="text-sm font-medium text-neutral-700">Your email</label>
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass + ' mt-1'} placeholder="name@example.com" />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Category</label>
            <select value={form.category} onChange={(e) => set('category', e.target.value)} className={inputClass + ' mt-1'}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Subject</label>
            <input value={form.subject} onChange={(e) => set('subject', e.target.value)} className={inputClass + ' mt-1'} placeholder="How can we help?" />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Message</label>
            <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={5} className={inputClass + ' mt-1 resize-none'} placeholder="Tell us more…" />
          </div>
          {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={submitting} className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Sending…' : 'Send message'}
          </button>
        </form>
      </SettingCard>
    </div>
  );
}
